import {
  Breakpoint,
  TaskSegment,
  TrimRange,
  VideoPlayerErrorCause,
  VideoPlayerErrorOverlayVariant,
  VideoPlayerLoadingOverlayVariant,
  VideoPlayerState,
} from './ui.video-player.types';

/**
 * Format time in seconds to MM:SS or HH:MM:SS format
 */
export const formatTime = (time: number): string => {
  if (!isFinite(time) || time < 0) return '0:00';

  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time % 3600) / 60);
  const seconds = Math.floor(time % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Resolve the duration reported by the media element against the currently
 * known duration. MediaRecorder WebM recordings report `Infinity` (and
 * occasionally `0` / `NaN`) until playback resolves the real value; accepting
 * those would overwrite the prop-derived duration and collapse the timeline
 * (positions divide by `Infinity` → 0). Keep the known duration until a finite,
 * positive value arrives.
 */
export const resolveReportedDuration = (
  reportedDuration: number,
  currentDuration: number
): number =>
  Number.isFinite(reportedDuration) && reportedDuration > 0
    ? reportedDuration
    : currentDuration;

/**
 * Task navigation and clip trimming are mutually exclusive timeline layouts:
 * the trim handles live only on the standard timeline, so while a clip is being
 * trimmed the task-navigation layout MUST NOT take over — otherwise the trim UI
 * silently disappears. Trim mode therefore overrides the caller's
 * `showTaskNavigation` request.
 */
export const resolveTaskNavigationEnabled = (
  showTaskNavigation: boolean,
  isTrimMode: boolean
): boolean => showTaskNavigation && !isTrimMode;

/**
 * Lay out task-navigation segments using explicit `time / duration`
 * percentages so the timeline shares the playhead's linear coordinate space.
 * Each segment maps to absolute video time, so the active segment's fill edge
 * lands exactly at `displayTime / duration` — the playhead position — with no
 * drift between the fill and the thumb.
 *
 * The first segment extends back to time 0 (not its breakpoint time) so a
 * pre-first-question stretch that emits no time-0 primary — e.g. a
 * URL-redirect intro — is absorbed into the first segment instead of rendering
 * as an empty gap at the start of the timeline.
 *
 * Because positions are absolute (not flex-grow weights), breakpoints that
 * collapse onto the same time — e.g. when wall-clock drift clamps several late
 * questions to the recording duration — produce `widthPercent: 0` segments that
 * simply disappear instead of pushing the fill out of sync with the thumb.
 */
export const computeTaskSegments = (
  breakpoints: Breakpoint[],
  duration: number,
  displayTime: number
): TaskSegment[] => {
  if (breakpoints.length === 0) return [];
  return breakpoints.map((bp, i) => {
    const startTime = i === 0 ? 0 : bp.time;
    const endTime =
      i < breakpoints.length - 1
        ? breakpoints[i + 1].time
        : duration || bp.time;
    const segDuration = Math.max(0, endTime - startTime);
    let fillRatio = 0;
    if (displayTime >= endTime && endTime > startTime) fillRatio = 1;
    else if (displayTime <= startTime) fillRatio = 0;
    else if (segDuration > 0)
      fillRatio = (displayTime - startTime) / segDuration;
    return {
      id: bp.id,
      leftPercent:
        duration > 0
          ? Math.min(Math.max((startTime / duration) * 100, 0), 100)
          : 0,
      widthPercent:
        duration > 0
          ? Math.min(Math.max((segDuration / duration) * 100, 0), 100)
          : 0,
      fillRatio,
    };
  });
};

export const getActiveBreakpointIndex = (
  currentTime: number,
  breakpoints: Breakpoint[]
): number => {
  if (breakpoints.length === 0) return -1;
  let activeIndex = -1;
  for (let i = 0; i < breakpoints.length; i++) {
    if (breakpoints[i].time <= currentTime) {
      activeIndex = i;
    } else {
      break;
    }
  }
  return activeIndex;
};

/**
 * Calculate breakpoint position as percentage of timeline
 */
export const calculateBreakpointPosition = (
  breakpointTime: number,
  duration: number
): number => {
  if (!duration || duration <= 0) return 0;
  return Math.min(Math.max((breakpointTime / duration) * 100, 0), 100);
};

/**
 * Check if a breakpoint should be triggered based on current time
 */
const shouldTriggerBreakpoint = (
  currentTime: number,
  breakpoint: Breakpoint,
  reachedBreakpoints: Set<string>,
  tolerance = 0.5
): boolean => {
  return (
    Math.abs(breakpoint.time - currentTime) <= tolerance &&
    !reachedBreakpoints.has(breakpoint.id)
  );
};

/**
 * Find breakpoint that should be triggered
 */
export const findTriggeredBreakpoint = (
  currentTime: number,
  breakpoints: Breakpoint[],
  reachedBreakpoints: Set<string>,
  tolerance = 0.5
): Breakpoint | undefined => {
  return breakpoints.find((bp) =>
    shouldTriggerBreakpoint(currentTime, bp, reachedBreakpoints, tolerance)
  );
};

/**
 * Calculate click position on timeline as percentage
 */
export const calculateTimelineClickPosition = (
  event: { clientX: number },
  timelineElement: HTMLElement
): number => {
  const rect = timelineElement.getBoundingClientRect();
  const clickX = event.clientX - rect.left;
  const timelineWidth = rect.width;
  return clickX / timelineWidth;
};

/**
 * Convert timeline click percentage to video time
 */
export const timelinePercentageToTime = (
  percentage: number,
  duration: number
): number => {
  return percentage * duration;
};

/**
 * Check if fullscreen is currently active
 */
function readHostProperty(target: object, key: string): unknown {
  return key in target ? Reflect.get(target, key) : undefined;
}

export const isFullscreenActive = (): boolean => {
  if (document.fullscreenElement) return true;
  return [
    'webkitFullscreenElement',
    'mozFullScreenElement',
    'msFullscreenElement',
  ].some((key) => Boolean(readHostProperty(document, key)));
};

export function callHostMethod(target: object, key: string): boolean {
  const value = readHostProperty(target, key);
  if (typeof value !== 'function') return false;
  value.call(target);
  return true;
}

export const clamp = (value: number, min: number, max: number): number => {
  return Math.min(Math.max(value, min), max);
};

/**
 * Resolve the final playback time a seek should jump to.
 *
 * This is the single guard in front of `HTMLMediaElement.currentTime`: every
 * seek path funnels through it. Clamping against a trim range can turn an
 * otherwise-finite input into `NaN` when a bound is non-finite (undefined/NaN
 * clip or breakpoint data), so the finiteness check MUST happen on the clamped
 * result, not the raw input. Returns `null` when the target is not a safe,
 * finite time — callers MUST skip seeking in that case.
 */
export const resolveSeekTarget = (
  time: number,
  trimRange?: TrimRange | null
): number | null => {
  const target = trimRange
    ? clamp(time, trimRange.startTime, trimRange.endTime)
    : time;

  return Number.isFinite(target) ? target : null;
};

const MEDIA_LOADING_STARTED_EVENTS: ReadonlySet<string> = new Set([
  'loadstart',
  'seeking',
  'waiting',
]);

const MEDIA_LOADING_SETTLED_EVENTS: ReadonlySet<string> = new Set([
  'loadedmetadata',
  'loadeddata',
  'canplay',
  'seeked',
  'playing',
  'error',
]);

const MEDIA_HAVE_NOTHING = 0;
const MEDIA_NETWORK_IDLE = 1;

export type MediaLoadingSnapshot = {
  readyState: number;
  networkState: number;
};

export const resolveMediaLoading = (eventType: string): boolean | null => {
  if (MEDIA_LOADING_STARTED_EVENTS.has(eventType)) return true;
  if (MEDIA_LOADING_SETTLED_EVENTS.has(eventType)) return false;
  return null;
};

const MEDIA_HAVE_CURRENT_DATA = 2;

export const isMediaReadyToRender = (media: {
  readyState: number;
  seeking: boolean;
}): boolean => media.readyState >= MEDIA_HAVE_CURRENT_DATA && !media.seeking;

const MEDIA_HAVE_METADATA = 1;

export const isSuspendedWithMetadata = (media: {
  readyState: number;
  seeking: boolean;
}): boolean => media.readyState >= MEDIA_HAVE_METADATA && !media.seeking;

export const resolveLoadingOverlayVariant = ({
  hasError,
  isCovering,
  isBuffering,
}: {
  hasError: boolean;
  isCovering: boolean;
  isBuffering: boolean;
}): VideoPlayerLoadingOverlayVariant | null => {
  if (hasError) return null;
  if (isCovering) return 'cover';
  return isBuffering ? 'buffering' : null;
};

export const resolveMediaIdentity = (src: string): string => {
  try {
    const url = new URL(src, 'http://local.invalid');
    return `${url.origin}${url.pathname}`;
  } catch {
    return src;
  }
};

export const resolveMediaErrorCause = (
  hasRenderedFrame: boolean
): VideoPlayerErrorCause => (hasRenderedFrame ? 'load' : 'processing');

export const resolveErrorOverlayVariant = (
  cause: VideoPlayerErrorCause
): VideoPlayerErrorOverlayVariant =>
  cause === 'processing' ? 'processing' : 'load';

export const isPlayBlockedByBrowser = (error: unknown): boolean =>
  (error instanceof DOMException || error instanceof Error) &&
  error.name === 'NotAllowedError';

const MEDIA_ERR_ABORTED = 1;

export const shouldReportMediaError = (code: number | undefined): boolean =>
  code !== MEDIA_ERR_ABORTED;

export const isIdleWithoutData = (media: MediaLoadingSnapshot): boolean =>
  media.readyState === MEDIA_HAVE_NOTHING &&
  media.networkState === MEDIA_NETWORK_IDLE;

export const shouldPauseAtTrimEnd = (
  previousTime: number,
  nextTime: number,
  trimRange: TrimRange
): boolean =>
  previousTime <= trimRange.endTime && nextTime >= trimRange.endTime;

export const resolvePlayStartTime = ({
  currentTime,
  duration,
  trimRange,
  resumeOutsideTrim,
}: {
  currentTime: number;
  duration?: number;
  trimRange?: TrimRange | null;
  resumeOutsideTrim: boolean;
}): number | null => {
  if (!trimRange) {
    return duration && currentTime >= duration ? 0 : null;
  }

  const isInTrimRange =
    currentTime >= trimRange.startTime && currentTime < trimRange.endTime;
  if (isInTrimRange) return null;

  const canResumeInPlace =
    resumeOutsideTrim && (!duration || currentTime < duration);
  return canResumeInPlace ? null : trimRange.startTime;
};

export const resolveMediaErrorState = (
  showsErrorPanel: boolean,
  message: string
): Partial<VideoPlayerState> =>
  showsErrorPanel
    ? {
        hasError: true,
        errorMessage: message,
        isLoading: false,
        isPlaying: false,
      }
    : { isPlaying: false, isLoading: false };

interface ReloadableMedia {
  readonly error: object | null;
  currentTime: number;
  load: () => void;
}

export const reloadErroredMedia = (media: ReloadableMedia | null): boolean => {
  if (!media?.error) return false;
  const resumeAt = media.currentTime;
  media.load();
  media.currentTime = resumeAt;
  return true;
};

export const resolveSourceSwapState = (
  previousSrc: string,
  nextSrc: string
): Partial<VideoPlayerState> | null =>
  previousSrc === nextSrc
    ? null
    : { hasError: false, errorMessage: '', isLoading: true, currentTime: 0 };
